'use strict'

import { ga4Event } from '@/libs/Google/GoogleTagManager.lib'
import { getBreakpoint } from '@/utils/app/detect'
import type { MetaData } from '@/types/cms'
import { reloadAds } from '@/libs/AdAlliance/AdAlliance.lib'

export const internalTracking = (url: string) => {
  const sourceId = new URL(window.location.href).searchParams.get('id')

  let source = '&source=direct'
  if (document.referrer) {
    source = '&source=' + encodeURI(document.referrer)
  } else if (sourceId) {
    source = '&source=push&sourceId=' + sourceId
  }

  new Image().src = url + '&platform=' + (getBreakpoint() === 'xs' ? 'mobile' : 'desktop') + source
}

export const vgwortTracking = (url: string) => {
  new Image().src = url
}

export const ga4EventTracking = (metadata: MetaData) => {
  trackTabVisibility()
  // only homepage desk
  if (location.pathname === '/' && (getBreakpoint() === 'lg' || getBreakpoint() === 'md')) {
    trackHomeSidebarTeaser()
  }
  // only homepage
  if (location.pathname === '/') {
    trackHomeScroll()
  }

  // only storyline
  if (metadata.model.article?.type === 'storyline' || metadata.model.article?.type === 'storyline-regio') {
    trackArticleScroll()
  }
}

const trackArticleScroll = () => {
  const allArticleP = document.querySelectorAll('.wrapper-article > p')
  if (allArticleP.length === 0) return

  const track = () => {
    ga4Event({
      eventcategory: 'scrollArticle',
      eventaction: scrollMark,
      eventlabel: window.location.href,
      eventvalue: Math.round((new Date().getTime() - timeStart) / 1000),
    })
  }

  const firstP = allArticleP[0]
  const lastP = allArticleP[allArticleP.length - 1]
  const intervall = [0, 10, 25, 50, 75, 90, 100]
  const timeStart = new Date().getTime()
  let txtHeight = lastP.getBoundingClientRect().bottom - firstP.getBoundingClientRect().top
  let txtSrollPercentage = 0
  let scrollMark = 0
  let lastScrollMark = 0

  const evalScroll = () => {
    txtSrollPercentage = ((-firstP.getBoundingClientRect().top + window.innerHeight) * 100) / txtHeight

    for (const i in intervall) {
      if (txtSrollPercentage >= intervall[i]) {
        scrollMark = intervall[i]
      }
    }

    if (scrollMark > lastScrollMark) {
      lastScrollMark = scrollMark
      track()
    }

    if (scrollMark === intervall[intervall.length - 1]) {
      window.removeEventListener('scroll', srollAction)
      window.removeEventListener('resize', resizeAction)
    }
  }

  const srollAction = () => {
    evalScroll()
  }

  const resizeAction = () => {
    txtHeight = lastP.getBoundingClientRect().bottom - firstP.getBoundingClientRect().top
    evalScroll()
  }

  // fire initial tracking
  track()

  window.addEventListener('scroll', srollAction)
  window.addEventListener('resize', resizeAction)
}

const trackTabVisibility = () => {
  let lastChangeTime = new Date().getTime()

  document.addEventListener('visibilitychange', function () {
    const changeTime = new Date().getTime()
    const elapsedTime = Math.round((changeTime - lastChangeTime) / 1000)
    lastChangeTime = changeTime

    if (document.visibilityState === 'visible') {
      ga4Event({
        eventcategory: 'tab_status_changed',
        eventaction: 'tab_activated',
        tab_activated_time_elapsed: elapsedTime,
      })
      reloadAds()
    } else {
      ga4Event({
        eventcategory: 'tab_status_changed',
        eventaction: 'tab_inactivated',
        tab_activated_time_elapsed: elapsedTime,
      })
    }
  })
}

const trackHomeSidebarTeaser = () => {
  document.querySelectorAll("[class^='group-grid-sidebar_sidebar'] article").forEach((value, index) => {
    value.querySelectorAll('a').forEach((a) => {
      a.onclick = () => {
        ga4Event({
          eventcategory: 'home_teaser_applet-column',
          eventaction: index + 1,
          eventlabel: location.origin + a.getAttribute('href'),
        })
      }
    })
  })
}

const trackHomeScroll = () => {
  const liveTickerElems = Array.prototype.slice
    .call(document.querySelectorAll("[class^='widget-teaser-liveticker_ticker']"))
    .filter((el) =>
      el.querySelector("[class*='widget-teaser-liveticker_kicker']").textContent.toLowerCase().includes('liveticker'),
    )

  const defautTeaserElems = [
    ...(document.querySelector("[class^='widget-teaser-default']")
      ? Array.prototype.slice.call(
          document.querySelector("[class^='widget-teaser-default']")?.querySelectorAll('article'),
        )
      : []),
  ]

  const imgLeftTeaserElems = [
    ...(document.querySelector("[class^='widget-teaser-img-left']")
      ? Array.prototype.slice.call(
          document.querySelector("[class^='widget-teaser-img-left']")?.querySelectorAll('article'),
        )
      : []),
  ]

  const trackingList: { [key: string]: HTMLElement | undefined | null } = {
    ticker_ukraine: liveTickerElems[0],
    dayteaser_red: document
      .querySelector("[class^='widget-teaser-day_teaser'] [class*='color-red']")
      ?.closest("[class^='widget-teaser-day_teaser']"),
    dayteaser_blue: document
      .querySelector("[class^='widget-teaser-day_teaser'] [class*='color-blue']")
      ?.closest("[class^='widget-teaser-day_teaser']"),
    dayteaser_green: document
      .querySelector("[class^='widget-teaser-day_teaser'] [class*='color-green']")
      ?.closest("[class^='widget-teaser-day_teaser']"),
    newsteaser_big_1: defautTeaserElems[0],
    newsteaser_big_2: defautTeaserElems[1],
    newsteaser_big_3: defautTeaserElems[2],
    newsteaser_small_1: imgLeftTeaserElems[0],
    newsteaser_small_2: imgLeftTeaserElems[1],
    newsteaser_small_3: imgLeftTeaserElems[2],
  }

  const findEventAction = (value: HTMLElement) => Object.keys(trackingList).find((key) => trackingList[key] === value)

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target)
          ga4Event({
            eventcategory: 'scroll',
            eventlabel: getBreakpoint(),
            eventaction: findEventAction(entry.target as HTMLElement),
          })
        }
      })
    },
    { root: null, rootMargin: '0px', threshold: 1 },
  )

  for (const key in trackingList) {
    if (trackingList[key]) observer.observe(trackingList[key])
  }
}
